import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../../components/Button';
import { getFromLocalStorage } from '../../../../global/helpers';
import * as ordersActions from '../../../OrdersPage/actions';
import { OrderTableData, UserRole } from '../../../OrdersPage/types';
import * as orderActions from '../../actions';
import { formatOrderItemsInfo } from '../../helpers';
import { selectors } from '../../reducer';
import PaymentIndicator from '../OrderProductsBlock/components/PaymentIndicator';
import AgreementView from './components/AgreementView';
import CartHistoryModal from './components/CartHistoryModal';
import CollectProgress from './components/CollectProgress';
import CollectTabs from './components/CollectTabs';
import ProductCollectCard from './components/ProductCollectCard';
import {
  buildFinalOrderItems,
  buildSendEditItems,
  filterProductsByTab,
  formatTenge,
  getCollectCounts,
  getCurrentOrderTotal,
  getStatusCounts,
  hasSendEditReasons,
  isCollectEditable,
  isProblemStatus,
} from './helpers';
import * as Styled from './OrderCollectBlock.styles';
import {
  AgreementDecision,
  AgreementDecisions,
  CollectPhase,
  CollectProductData,
  CollectStatus,
  CollectTab,
  ProductExtraOptions,
  ProductExtras,
  ProductMark,
  ProductMarks,
} from './types';

interface OrderCollectBlockProps {
  data: OrderTableData;
}

const OrderCollectBlock = ({ data }: OrderCollectBlockProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userRole = getFromLocalStorage('role');
  const token = getFromLocalStorage('accessToken');
  const isSaving = useSelector(selectors.putOrderLoading);

  const [phase, setPhase] = useState<CollectPhase>(CollectPhase.COLLECT);
  const [activeTab, setActiveTab] = useState<CollectTab>(CollectTab.PENDING);
  const [marks, setMarks] = useState<ProductMarks>({});
  const [extras, setExtras] = useState<ProductExtras>({});
  const [decisions, setDecisions] = useState<AgreementDecisions>({});
  const [isHistoryOpen, setIsHistoryOpen] = useState<boolean>(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const showToast = (message: string, severity: 'success' | 'warning') => {
    setToast({ open: true, message, severity });
  };

  const closeToast = () => setToast((prev) => ({ ...prev, open: false }));

  const products = useMemo(
    () => (data.products || []) as CollectProductData[],
    [data.products]
  );

  const editable = isCollectEditable(data);
  const counts = getCollectCounts(products, marks);
  const statusCounts = getStatusCounts(products, marks);
  const visibleProducts = filterProductsByTab(products, marks, activeTab);
  const isAllChecked = counts.pending === 0;
  const orderTotal = getCurrentOrderTotal(products, marks, decisions);

  const handleSetMark = (product: CollectProductData, mark: ProductMark) => {
    setMarks((prev) => ({ ...prev, [product.sku]: mark }));
    // сброс ранее принятого решения по этому товару
    setDecisions((prev) => {
      const next = { ...prev };
      delete next[product.sku];
      return next;
    });
    // подсказка, куда переместился товар после смены статуса
    const isProblem = isProblemStatus(mark.status);
    showToast(
      t(
        isProblem
          ? 'OrderPage.Collect.Toast.MOVED_PROBLEMS'
          : 'OrderPage.Collect.Toast.MOVED_DONE'
      ),
      isProblem ? 'warning' : 'success'
    );
  };

  const handleResetMark = (product: CollectProductData) => {
    setMarks((prev) => {
      const next = { ...prev };
      delete next[product.sku];
      return next;
    });
    setDecisions((prev) => {
      const next = { ...prev };
      delete next[product.sku];
      return next;
    });
  };

  const handleDecision = (sku: string, decision: AgreementDecision | null) => {
    setDecisions((prev) => {
      const next = { ...prev };
      if (decision) {
        next[sku] = decision;
      } else {
        delete next[sku];
      }
      return next;
    });
  };

  const handleExtraChange = (
    product: CollectProductData,
    changes: Partial<ProductExtraOptions>
  ) => {
    setExtras((prev) => ({
      ...prev,
      [product.sku]: { ...prev[product.sku], ...changes },
    }));
  };

  // После сохранения все оставшиеся товары считаются проверенными
  const markKeptProductsAsCollected = (keptItems: CollectProductData[]) => {
    const collectedMarks: ProductMarks = {};
    keptItems.forEach((item) => {
      collectedMarks[item.sku] = { status: CollectStatus.IN_STOCK };
    });
    setMarks(collectedMarks);
    setExtras({});
    setDecisions({});
    setPhase(CollectPhase.COLLECT);
    setActiveTab(CollectTab.DONE);
  };

  const refreshOrder = () => {
    const role = userRole === UserRole.MANAGER ? 'network' : 'pharmacy';
    dispatch(
      ordersActions.setSelectedOrder.request({
        role,
        id: data.id,
        testOrders: data.test,
      })
    );
  };

  // Сохранение: правим корзину (putOrder) → причины (sendEdit) →
  // переводим заказ в «Собран» (putReadyStatus → IN_PHARMACY_COLLECTED)
  const handleSave = () => {
    const finalItems = buildFinalOrderItems(products, marks, decisions);
    if (!finalItems.length) return;

    const orderInfo = formatOrderItemsInfo(
      data.address ? data.address.id : 0,
      data.deliveryType,
      data.paymentType,
      '',
      data.source,
      data.paid,
      data.paidDelivery,
      finalItems
    );

    const sendReasons = hasSendEditReasons(products, marks, extras);
    const sendEditItems = buildSendEditItems(products, marks, extras);

    dispatch(
      orderActions.putOrder.request({
        orderInfo,
        id: data.id,
        postEffect: () => {
          if (sendReasons) {
            dispatch(
              orderActions.sendEdit.request({
                id: data.id,
                data: sendEditItems,
                postEffect: () => {},
              })
            );
          }
          dispatch(
            ordersActions.putReadyStatus.request({
              id: data.id,
              token,
              postEffect: () => {
                markKeptProductsAsCollected(finalItems);
                refreshOrder();
              },
            })
          );
        },
      })
    );
  };

  const renderFooter = () => {
    if (!isAllChecked) {
      return (
        <span className="pending-info">
          {t('OrderPage.Collect.Footer.PENDING', { quantity: counts.pending })}
        </span>
      );
    }
    // Есть проблемные позиции → согласование с клиентом (шаг 2)
    if (counts.problems > 0) {
      return (
        <div className="footer-action">
          <Button
            text={t('OrderPage.Collect.Footer.NEXT_AGREEMENT', {
              quantity: counts.problems,
            })}
            variant="contained"
            onClick={() => setPhase(CollectPhase.AGREEMENT)}
          />
        </div>
      );
    }
    // Проблем нет → «Сохранить» (переводит заказ в «Собран»)
    return (
      <div className="footer-action">
        <Button
          text={t('OrderPage.Collect.Footer.SAVE')}
          variant="contained"
          disabled={isSaving}
          isLoading={isSaving}
          onClick={handleSave}
        />
      </div>
    );
  };

  const orderTotalBar = (
    <Styled.OrderTotalBar>{formatTenge(orderTotal)}</Styled.OrderTotalBar>
  );

  if (phase === CollectPhase.AGREEMENT && editable) {
    return (
      <Styled.CollectContainer id="products">
        {orderTotalBar}
        <PaymentIndicator paid={data.paid} role={userRole} />
        <AgreementView
          products={products}
          marks={marks}
          decisions={decisions}
          customerPhone={data.phone}
          userSource={data.userSource}
          isSaving={isSaving}
          onDecision={handleDecision}
          onBack={() => setPhase(CollectPhase.COLLECT)}
          onSave={handleSave}
        />
      </Styled.CollectContainer>
    );
  }

  return (
    <Styled.CollectContainer id="products">
      {orderTotalBar}
      <PaymentIndicator paid={data.paid} role={userRole} />
      <Styled.StepHeader>
        <div className="step-top">
          {editable && (
            <span className="step-badge">{t('OrderPage.Collect.STEP')}</span>
          )}
          <button
            type="button"
            className="history-button"
            onClick={() => setIsHistoryOpen(true)}
          >
            {t('OrderPage.Collect.HISTORY')}
          </button>
        </div>
        <div className="step-title-row">
          <span className="step-title">
            {t(
              editable
                ? 'OrderPage.Collect.STEP_TITLE'
                : 'OrderPage.Collect.STEP_TITLE_DONE'
            )}
          </span>
          <span className="products-count">
            {t('OrderPage.Collect.PRODUCTS_COUNT', { count: products.length })}
          </span>
        </div>
      </Styled.StepHeader>

      {editable && (
        <>
          <CollectProgress counts={counts} statusCounts={statusCounts} />
          <CollectTabs
            activeTab={activeTab}
            counts={counts}
            onChange={setActiveTab}
          />
        </>
      )}

      {visibleProducts.length === 0 ? (
        <Styled.EmptyTab>
          <div className="empty-text">
            {t(`OrderPage.Collect.EmptyTab.${activeTab.toUpperCase()}`)}
          </div>
          {activeTab === CollectTab.PENDING ? (
            <button
              type="button"
              className="show-all-button"
              onClick={() => setActiveTab(CollectTab.DONE)}
            >
              {t('OrderPage.Collect.EmptyTab.SHOW_DONE')}
            </button>
          ) : (
            <button
              type="button"
              className="show-all-button"
              onClick={() => setActiveTab(CollectTab.PENDING)}
            >
              {t('OrderPage.Collect.EmptyTab.SHOW_PENDING')}
            </button>
          )}
        </Styled.EmptyTab>
      ) : (
        <Styled.ProductsList>
          {visibleProducts.map((product) => (
            <ProductCollectCard
              key={product.sku}
              product={product}
              mark={marks[product.sku]}
              extra={extras[product.sku] || {}}
              editable={editable}
              onSetMark={handleSetMark}
              onResetMark={handleResetMark}
              onExtraChange={handleExtraChange}
            />
          ))}
        </Styled.ProductsList>
      )}

      {editable && (
        <Styled.CollectFooter>{renderFooter()}</Styled.CollectFooter>
      )}

      {isHistoryOpen && (
        <CartHistoryModal
          orderId={data.id}
          createdAt={data.originalCreatedAt}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Styled.CollectContainer>
  );
};

export default OrderCollectBlock;
